import {action, createTransformer, observable, runInAction} from 'mobx';
import orderBy from 'lodash/orderBy';
import isEmpty from 'lodash/isEmpty';
import Player from '../../../stores/PlayerStore';
import request from '../../../utils/request';
import {getId, getNewSenderData, isStudent, isTeacher} from '../../../utils/userUtility';
import {getColorByTier} from '../../../themes/colors';
import {CHAT} from '../../../constants/urlConstants';
import {customEvent} from '../../../utils/analyticsUtil';
import {category, events, LessonNotificationType, NotificationCategoryType, NotificationType} from '../../../constants/enums';
import projectStore from '../../main/project/store';
import realtimeStore from '../../../stores/RealtimeStore';
import mainStore from "../../main/store";
import videoChatStore from "../../video-chat/store";
import wizardStore from "../../studio/store";
import {guid} from "../../../utils/commonUtility";
import {uploadToS3} from "../../../utils/awsUtility";
import {ATTACHMENT_BUCKET} from "../../../constants/constants";


class ChatStore extends Player {
    @observable chatMap = new Map();
    @observable message = '';
    @observable activeChat = null;
    @observable fetching = false;
    @observable showMessagesBack = false;
    @observable isFileLoading = false;

    async loadChat(chatID, type) {
        if (!this.chatMap.has(chatID)) {
            await this.initChat(chatID, type);
        }
    }

    @action
    setActiveChat(id) {
        this.activeChat = id;
        this.loadChat(id, 'student');
    }

    serializeChatMessage = (chat, message) => {
        if (!isEmpty(message.audioData)) {
            this.initTrack(message.audioData);
        }
        return message;
    };

    deserializeTrack = (track) => {
        return {
            id: track.id,
            fileId: track.fileId,
            name: track.name,
            bpm: track.bpm,
            originalBPM: track.originalBPM,
            trackActionData: track.trackActionData,
            playColor: track.playColor,
            startTime: track.startTime,
            endTime: track.endTime
        };
    };

    /**
     * get chat by chatID
     * @type {ITransformer<any, any>}
     */
    chat = createTransformer(id => this.chatMap.get(id));

    /**
     * Set up or load a new chat to the session
     * @param chatID
     * @param type
     * @returns {Promise<void>}
     */
    @action
    async initChat(chatID, type) {
        this.chatMap.set(chatID, {
            id: chatID,
            type,
            history: [],
            draftMessage: '',
            audioAttachment: this.initTrack({})
        });
        const chatHistory = await this.fetchChatHistory(chatID, type);
        // const projectLoopers = projectStore.activeProject.loopers.slice();

        chatHistory.forEach((msg) => {
            if (msg.audioData) {
                const msgId = msg.audioData.id;
                // for (let i = 0; i < projectLoopers.length; i += 1) {
                //     if (projectLoopers[i].id === msgId) {
                //         msg.color = getColorByTier(6 - i);
                //         break;
                //     }
                // }
            }
        });

        runInAction(() => {
            const chat = this.chatMap.get(chatID);
            chat.history = chatHistory;
            if (chatHistory[0]) {
                chat.student = chatHistory[0].student;
                chat.teacher = chatHistory[0].teacher;
            }
            // try {
            //     await realtimeStore.subscribe(`${type}:${chatID}`, (client, channel, message) => {
            //         this.onReceiveNewMessage(chatID, client, channel, message);
            //     });
            // } catch (err) {
            //     console.error(err);
            // }
        });
    }

    /**
     * Set a track message to chat
     * @param chatID
     * @param trackMessage
     * @param type
     */
    @action
    async setAudioAttachment(chatID, trackMessage, type = 'project') {
        let chat = this.chatMap.get(chatID);
        if (isEmpty(chat)) {
            try {
                await this.fetchChatHistory(chatID, type);
                chat = this.chatMap.get(chatID);
            } catch (err) {
                throw err;
            }
        }
        runInAction(() => {
            if (!isEmpty(chat)) {
                chat.audioAttachment = this.initTrack(trackMessage);
            }
        });
    }

    /**
     * Clear a track message from a chat
     * @param chatID
     */
    @action
    clearAudioAttachment(chatID) {
        if (this.chatMap.has(chatID)) {
            this.clearPlayer(this.chatMap.get(chatID).audioAttachment);
            this.chatMap.get(chatID).audioAttachment = {};
        }
    }

    /**
     * Push a new project chat message from socket
     * @param chatID {String}    The chat to sub to
     * @param client    {Object}    The socket client
     * @param channel   {String}    The channel we subscribed to
     * @param data   {Object}    The data
     */
    @action
    onReceiveNewMessage(chatID, data) {
        const chat = this.chatMap.get(chatID);
        if (chat && data.from.id !== getId()) {
            chat.history.push(this.serializeChatMessage(chat, data));
        }
        if (isStudent()) {
            this.setActiveChat(data.teacher.id);
            mainStore.openMainAppDrawer();
        }
        if (isTeacher() && (videoChatStore.videoChatStarted && data.student.id === videoChatStore.getPeer || wizardStore.presentMode)) {
            this.setActiveChat(data.student.id);
            mainStore.openMainAppDrawer();
        }
    }

    /**
     * Set a new message to project chat
     * @param newMessage    {String}    The new user message
     * @param chatID
     * @param file
     */
    @action submitNewMessage = (newMessage, chatID, file = false) => {
        const chat = this.chatMap.get(chatID);
        const projectLoopers = (projectStore.activeProject && projectStore.activeProject.loopers && projectStore.activeProject.loopers.slice()) || [];
        let atchColor = '';
        if (chat) {
            const audioData = !isEmpty(chat.audioAttachment.id) ? this.deserializeTrack(chat.audioAttachment) : null;
            if (audioData !== null) {
                customEvent(events.MusicMessageSend, {context: chatID});
                for (let i = 0; i < projectLoopers.length; i += 1) {
                    if (projectLoopers[i].id === audioData.id) {
                        atchColor = getColorByTier(6 - i);
                        break;
                    }
                }
            } else {
                customEvent(events.CustomEvent, {category: category.SendMessage});
            }
            let data = this.makeMessageObj(newMessage, chat, file, audioData, atchColor);
            if (!file) { // for files we already push to history when they are uploaded
                chat.history.push(data);
            }

            realtimeStore.send(`general:${chat.id}`, data);
            request(`${CHAT}/${chat.id}`, {
                method: 'POST',
                body: data
            });
            this.clearAudioAttachment(chatID);
        }
    };


    makeMessageObj(newMessage, chat, file, audioData, atchColor) {
        let created = new Date();
        let senderData = getNewSenderData(this.activeChat);
        return {
            ...senderData,
            color: atchColor,
            message: newMessage,
            audioData: !isEmpty(audioData) ? this.initTrack(Object.assign({}, audioData)) : null,
            objectType: NotificationType.Lesson,
            objectId: senderData.fromId,
            type: LessonNotificationType.Chat,
            category: NotificationCategoryType.Messenger,
            timestamp: created.getTime(),
            created: created,
            count: 1,
            isRead: false,
            attachmentUrl: file.url
        };
    }

    /**
     * Fetch Project Chat
     * @returns {Array}
     */
    @action
    async fetchChatHistory(chatID) {
        const chat = this.chatMap.get(chatID);
        if (chat) {
            try {
                this.fetching = true;
                const chatHistory = await request(`${CHAT}/${chatID}`);
                // mark as read
                setTimeout(async () => {
                    await request(`${CHAT}/${chatID}`, {method: 'PUT'});
                }, 0);
                return orderBy(chatHistory.map(message => this.serializeChatMessage(chat, message)), 'timestamp');
            } catch (err) {
                return [];
            } finally {
                runInAction(() => {
                    this.fetching = false;
                });
            }
        }
        return [];
    }

    @action setMessage = (val) => {
        this.message = val;
    };

    @action clearMessage = () => {
        this.message = '';
    };

    @action toggleGoBackToMessagesMenu = () => {
        this.showMessagesBack = !this.showMessagesBack;
    }

    @action onChatFileChange = async (chatID, target) => {
        const files = Array.isArray(target) ? target : [target];

        const chat = this.chatMap.get(chatID);
        if (chat) {
            let filesToUpload = []
            files.forEach(file => {
                const fileObj = this.makeTempUploadObj(file);
                const fileMsg = this.makeMessageObj(fileObj.name, chat, fileObj);
                chat.history.push({...fileMsg, progress: 0, done: false});
                filesToUpload.push({file, fileObj, fileMsg});
            });

            await Promise.all(filesToUpload.map(async ({file, fileObj, fileMsg}) => {
                await this.saveChatFile(file, fileObj, fileMsg, chat, chatID);
            }))
        }
    };

    makeTempUploadObj = (file) => {
        const attachmentId = guid();
        const key = `attachments/${attachmentId}`;
        return {
            id: key,
            name: file.name,
            type: file.fixedType || file.type,
            url: '/' + key,
            done: false,
            userId: getId(),
        };
    }

    @action saveChatFile = async (file, fileObj, fileMsg, chat, chatID) => {
        const uploadChatInstance = {};
        const key = fileObj.id;
        const index = chat.history.findIndex(msg => msg.attachmentUrl === fileObj.url)
        const onUploadProgress = (fileId, {total, loaded}) => {
            if (index >= 0) {
                runInAction(() => {
                    const curr = chat.history[index].progress;
                    chat.history[index].progress = Math.max(curr, loaded / total * 100);
                })
            }
        }
        const promise = uploadToS3(file, key, file.type, onUploadProgress, ATTACHMENT_BUCKET, true, uploadChatInstance);

        try {
            await promise;
            this.submitNewMessage(fileObj.name, chatID, fileObj);
            runInAction(() => {
                if (index >= 0) {
                    chat.history[index].done = true;
                    chat.history[index].progress = 100;
                }
            });
        } catch (err) {
            console.log('error uploading file', err);
            runInAction(() => {
                // todo remove msg/mark
                if (index >= 0) {
                    chat.history.splice(index, 1);
                }
            });
        }
    };
}

const chatStore = new ChatStore();
export default chatStore;
